import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { Plus, Edit, Trash2, Search, Tag, Upload, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/Table'
import { Badge } from '../components/ui/Badge'
import { enumsApi, imagesApi } from '../lib/api'
import { useLanguage } from '../contexts/LanguageContext'

const languageLabels = {
  az: 'Azərbaycan',
  ru: 'Русский',
  en: 'English',
  ko: '한국어',
  kk: 'Қазақша',
  ky: 'Кыргызча'
}

export default function Options() {
  const [filters, setFilters] = useState({
    search: '',
    page: 1,
    limit: 10
  })
  const [deleteId, setDeleteId] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [formData, setFormData] = useState({
    name_az: '',
    name_ru: '',
    name_en: '',
    name_ko: '',
    name_kk: '',
    name_ky: '',
    price: '',
    image_url: ''
  })
  const [selectedImage, setSelectedImage] = useState(null)
  const [uploadingImage, setUploadingImage] = useState(false)
  const { currentLanguage } = useLanguage()
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery(
    ['options', filters],
    () => enumsApi.options.getAll(filters),
    {
      select: (data) => data.data?.data,
      keepPreviousData: true,
      retry: 1,
      refetchOnWindowFocus: false
    }
  )

  const deleteMutation = useMutation(
    (id) => enumsApi.options.delete(id),
    {
      onSuccess: () => {
        toast.success('Option deleted successfully')
        queryClient.invalidateQueries(['options'])
        setDeleteId(null)
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete option')
      }
    }
  )

  const createMutation = useMutation(
    (data) => enumsApi.options.create(data),
    {
      onSuccess: () => {
        toast.success('Option created successfully')
        queryClient.invalidateQueries(['options'])
        setShowForm(false)
        resetForm()
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create option')
      }
    }
  )

  const updateMutation = useMutation(
    (data) => enumsApi.options.update(editingId, data),
    {
      onSuccess: () => {
        toast.success('Option updated successfully')
        queryClient.invalidateQueries(['options'])
        setShowForm(false)
        setEditingId(null)
        resetForm()
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update option')
      }
    }
  )

  const [togglingId, setTogglingId] = useState(null)

  const toggleStatusMutation = useMutation(
    (id) => {
      setTogglingId(id)
      return enumsApi.options.toggleStatus(id)
    },
    {
      onSuccess: (response) => {
        toast.success(response.data?.message || 'Option status updated successfully')
        queryClient.invalidateQueries(['options'])
        setTogglingId(null)
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to toggle option status')
        setTogglingId(null)
      }
    }
  )

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1
    }))
  }

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this option?')) {
      deleteMutation.mutate(id)
    }
  }

  const handleToggleStatus = (id) => {
    toggleStatusMutation.mutate(id)
  }

  const handleEdit = (option) => {
    setEditingId(option.id)
    setFormData({
      name_az: option.name_az,
      name_ru: option.name_ru,
      name_en: option.name_en,
      name_ko: option.name_ko,
      name_kk: option.name_kk || option.name_ru,
      name_ky: option.name_ky || option.name_ru,
      price: option.price || '',
      image_url: option.image_url || ''
    })
    setSelectedImage(option.image_url || null)
    setShowForm(true)
  }

  const resetForm = () => {
    setFormData({
      name_az: '',
      name_ru: '',
      name_en: '',
      name_ko: '',
      name_kk: '',
      name_ky: '',
      price: '',
      image_url: ''
    })
    setSelectedImage(null)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (editingId) {
      updateMutation.mutate(formData)
    } else {
      createMutation.mutate(formData)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleImageUpload = async (file) => {
    if (!file) return

    setUploadingImage(true)
    try {
      const formData = new FormData()
      formData.append('images', file)

      const response = await imagesApi.upload(formData)
      const uploadedImage = response.data.data[0]

      setSelectedImage(uploadedImage.path)
      setFormData(prev => ({
        ...prev,
        image_url: uploadedImage.path
      }))

      toast.success('Image uploaded successfully')
    } catch (error) {
      toast.error('Failed to upload image')
      console.error('Image upload error:', error)
    } finally {
      setUploadingImage(false)
    }
  }

  const removeImage = () => {
    setSelectedImage(null)
    setFormData(prev => ({
      ...prev,
      image_url: ''
    }))
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Options</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage vehicle options and features
          </p>
        </div>
        <Button onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Option
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>
              {editingId ? 'Edit Option' : 'Add New Option'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Names in Different Languages</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Object.entries(languageLabels).map(([lang, label]) => (
                    <div key={lang}>
                      <label className="label">
                        Name ({label}) *
                        {lang === currentLanguage && (
                          <span className="ml-2 text-xs text-primary-600 font-medium">(Current Language)</span>
                        )}
                      </label>
                      <Input
                        placeholder={`Enter name in ${label}`}
                        value={formData[`name_${lang}`]}
                        onChange={(e) => handleInputChange(`name_${lang}`, e.target.value)}
                        required
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="label">Price</label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.price}
                    onChange={(e) => handleInputChange('price', e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className="label">Option Image</label>
                <div className="space-y-4">
                  {selectedImage ? (
                    <div className="relative inline-block">
                      <img
                        src={`${import.meta.env.VITE_IMAGE_URL}${selectedImage}`}
                        alt="Option"
                        className="w-32 h-32 object-cover rounded-lg"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute -top-2 -right-2 h-6 w-6 p-0 bg-red-500 hover:bg-red-600 text-white rounded-full"
                        onClick={removeImage}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                      <div className="text-center">
                        <Upload className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="mt-4">
                          <label htmlFor="option-image-upload" className="cursor-pointer">
                            <span className="mt-2 block text-sm font-medium text-gray-900">
                              Upload option image
                            </span>
                            <input
                              id="option-image-upload"
                              type="file"
                              accept="image/*"
                              className="sr-only"
                              onChange={(e) => handleImageUpload(e.target.files[0])}
                              disabled={uploadingImage}
                            />
                          </label>
                          <p className="mt-1 text-xs text-gray-500">
                            PNG, JPG, GIF up to 10MB
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowForm(false)
                    setEditingId(null)
                    resetForm()
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={createMutation.isLoading || updateMutation.isLoading}
                >
                  {editingId ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Search className="h-5 w-5 mr-2" />
            Search Options
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="max-w-md">
            <Input
              placeholder="Search options..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Options ({data?.count || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="text-center py-12">
              <p className="text-red-600">Error loading options: {error.message}</p>
            </div>
          ) : isLoading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                </div>
              ))}
            </div>
          ) : data?.results?.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Image</TableHead>
                    <TableHead>Has Standard</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.results.map((option) => (
                    <TableRow key={option.id}>
                      <TableCell>
                        <div className="flex items-center">
                          <Tag className="h-4 w-4 mr-2 text-gray-400" />
                          {option[`name_${currentLanguage}`] || option.name}
                        </div>
                      </TableCell>
                      <TableCell>
                        {option.price ? `$${parseFloat(option.price).toFixed(2)}` : '-'}
                      </TableCell>
                      <TableCell>
                        {option.image_url ? (
                          <img
                            src={`${import.meta.env.VITE_IMAGE_URL}${option.image_url}`}
                            alt={'Image error'}
                            className="w-12 h-12 object-cover rounded"
                          />
                        ) : (
                          <span className="text-gray-400 text-sm">No image</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <button
                          onClick={() => handleToggleStatus(option.id)}
                          disabled={togglingId === option.id}
                          className="cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                          title="Click to toggle status"
                        >
                          {togglingId === option.id ? (
                            <div className="flex items-center">
                              <div className="loading-spinner mr-2" />
                              <Badge variant={option.is_active ? 'success' : 'gray'}>
                                {option.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                            </div>
                          ) : (
                            <Badge variant={option.is_active ? 'success' : 'gray'}>
                              {option.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          )}
                        </button>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(option)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDelete(option.id)}
                            disabled={deleteMutation.isLoading && deleteId === option.id}
                          >
                            {deleteMutation.isLoading && deleteId === option.id ? (
                              <div className="loading-spinner" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className='flex justify-center gap-2 flex-wrap mt-4'>
                {
                  Array(data.pages).fill('').map((_, i) => <Button onClick={() => setFilters(p => ({ ...p, page: (i + 1) }))}> {i + 1} </Button>)
                }

              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <Tag className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No options</h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by adding your first option.
              </p>
              <div className="mt-6">
                <Button onClick={() => setShowForm(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Option
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
